import React, { Fragment } from "react";
import styled from "styled-components";
import SectionList from "../sections/SectionList";
import Content from "../common/Content";
import Headline from "../common/Headline";
import FullImageIntro from "../common/FullImageIntro";
import Paragraph from "../common/Paragraph";
import TopicGrid from "../common/TopicGrid";
import JobOfferTile from "../common/JobOfferTile";
import TopBox from "../common/TopBox";
import Label from "../common/Label";
import Grid from "../common/Grid";
import { ApiResult } from "../../lib/CmsApi";
import HeaderWithIntro from "../common/HeaderWithIntro";
import Contacts from "../sections/Contacts";
import Section from "../sections/Section";
import JobCategoryTile from "./JobCategoryTile";
import CustomPageHead from "../common/CustomPageHead";

const StyledSectionList = styled(SectionList)`
  > :first-child {
    padding-top: 0;
  }
`;

export type Props = ApiResult<"loadCareer">;
export default function CareerPage(props: Props) {
  return (
    <Fragment>
      <CustomPageHead
        title={props.name}
        desc={props.header && props.header.intro}
        seo={props.seo}
      />
      <TopBox
        breadCrumpPath={[
          {
            title: props.name,
            route: "career"
          }
        ]}
        subLinks={props.joboffers.jobCategories.map(cat => ({
          title: cat.name,
          route: "careerLevel",
          params: {
            slug: cat.slug
          }
        }))}
        title={props.name}
      />
      <main>
        {props.header && (
          <FullImageIntro
            image={props.header.image}
            icon={props.header.icon}
            headline={props.header.headline}
            fallBackAlt={props.header.headline}
            intro={props.header.intro}
          />
        )}
        {props.topjobs && (
          <Section noTopPadding>
            <Content padded>
              <Headline level={2}>{props.topjobs.title}</Headline>
              <Paragraph serif>{props.topjobs.subTitle}</Paragraph>
              <Headline size={3} level={3}>
                Unsere Top {props.topjobs.jobs.length} Stellenangebote
              </Headline>
              <Grid
                columns={props.topjobs.jobs.length}
                tabletColumns={Math.min(2, props.topjobs.jobs.length)}
                mobileColumns={1}
              >
                {props.topjobs.jobs.map((job, i) => {
                  return <JobOfferTile key={job.slug + i} {...(job as any)} />;
                })}
              </Grid>
            </Content>
          </Section>
        )}
        {props.joboffers && (
          <Section noTopPadding>
            <Content padded>
              <HeaderWithIntro
                headline={props.joboffers.title}
                intro={props.joboffers.intro}
                overline={props.joboffers.toptitle}
                size={3}
                level={2}
                serif
              />
              <Label small>Ihre Einstiegsmöglichkeiten:</Label>
              <TopicGrid columns={2} mobileColumns={1}>
                {props.joboffers.jobCategories.map((cat, i) => {
                  return (
                    <JobCategoryTile
                      categoryId={cat._id}
                      key={cat.slug + i}
                      type={cat.name}
                      headline={cat.headline}
                      text={cat.teaser}
                      cta="Zu den Stellenangeboten"
                      link={{
                        route: "careerLevel",
                        params: {
                          slug: cat.slug
                        }
                      }}
                    />
                  );
                })}
              </TopicGrid>
            </Content>
          </Section>
        )}
        <StyledSectionList sections={props.sections} />
        <Contacts
          headline="Wir sind für Sie da."
          copy={`Unsere Ansprechpartner für den Bereich „${props.name}“ stehen Ihnen gerne für Ihre Anliegen zur Verfügung.`}
          contacts={props.contacts}
          ariaLabel={`Ansprechpartner für den Bereich ${props.name}`}
          withImage
        />
      </main>
    </Fragment>
  );
}

CareerPage.getImages = (props: Props) => {
  let images = [];
  images = images.concat(FullImageIntro.getImages(props.header));
  images = images.concat(SectionList.getImages({ sections: props.sections }));
  images = images.concat(
    Contacts.getImages({ contacts: props.contacts, withImage: true })
  );

  return images;
};
